import { z } from 'zod';
import {
  AUTOMATION_ACTIONS, AUTOMATION_TRIGGERS, BILLING_INTERVAL,
  MESSAGE_CHANNEL, MESSAGE_DELIVERY, MESSAGE_DIRECTION,
} from '../enums.js';
import { EntityMetaSchema, zEmail, zEnum, zNonEmpty } from './common.js';

/** A communication (message/call/note) on the unified inbox, linked to a customer + optional entity. */
export const MessageSchema = EntityMetaSchema.extend({
  customerId: z.string(),
  channel: zEnum(MESSAGE_CHANNEL),
  direction: zEnum(MESSAGE_DIRECTION),
  delivery: zEnum(MESSAGE_DELIVERY).default('queued'),
  subject: z.string().max(200).optional(),
  body: z.string().max(20000).default(''),
  // Telephony fields
  fromNumber: z.string().max(25).optional(),
  toNumber: z.string().max(25).optional(),
  durationSec: z.number().int().min(0).optional(), // for calls
  recordingKey: z.string().optional(), // S3 key (private) for a call recording
  transcript: z.string().max(50000).optional(),
  aiSummary: z.string().max(4000).optional(),
  mediaKeys: z.array(z.string()).default([]), // MMS attachments (S3 keys)
  // Optional links to the entity the message concerns:
  relatedType: z.enum(['lead', 'job', 'estimate', 'invoice']).optional(),
  relatedId: z.string().optional(),
  authorUserId: z.string().optional(),
  providerMessageId: z.string().optional(),
  failureReason: z.string().max(500).optional(),
  needsResponse: z.boolean().default(false),
});
export type Message = z.infer<typeof MessageSchema>;

/** SMS consent record (A2P/TCPA). Required before outbound marketing/automation SMS. */
export const SmsConsentSchema = EntityMetaSchema.extend({
  customerId: z.string(),
  phone: z.string().max(25),
  status: z.enum(['opted_in', 'opted_out']).default('opted_in'),
  source: z.string().max(120), // e.g. "website form", "verbal - office", "reply START"
  consentLanguage: z.string().max(2000).optional(),
  consentVersion: z.string().max(40).optional(),
  capturedByUserId: z.string().optional(),
});
export type SmsConsent = z.infer<typeof SmsConsentSchema>;

/** A tenant business phone number (Worksy-provisioned via the comms provider). */
export const PhoneNumberSchema = EntityMetaSchema.extend({
  e164: z.string().max(25),
  label: z.string().max(80).optional(),
  provider: z.string().default('twilio'),
  providerNumberId: z.string().optional(),
  tollFree: z.boolean().default(false),
  smsEnabled: z.boolean().default(true),
  voiceEnabled: z.boolean().default(true),
  a2pStatus: z.enum(['not_registered', 'pending', 'approved', 'toll_free_pending']).default('not_registered'),
  /** Provider campaign the number is attached to (10DLC) once registered. */
  campaignId: z.string().optional(),
});
export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

/**
 * A2P 10DLC registration state for a tenant (one per tenant). Worksy drives the whole flow against
 * the comms provider so a tenant self-registers for business texting without touching a carrier
 * portal: register a Brand → (sole-prop) verify by SMS OTP → create a Campaign → attach the number.
 * `status` is Worksy's normalized lifecycle; `brandStatus`/`campaignStatus` keep the raw provider values.
 */
export const A2P_STATUS = [
  'not_started', 'brand_pending', 'otp_sent', 'brand_verified', 'campaign_pending', 'active', 'failed',
] as const;

export const A2pBusinessSchema = z.object({
  displayName: zNonEmpty.max(160),       // brand display name (shown to carriers)
  legalName: z.string().max(160).optional(), // standard brand: registered company name
  firstName: z.string().max(80).optional(),  // sole proprietor
  lastName: z.string().max(80).optional(),   // sole proprietor
  ein: z.string().max(20).optional(),        // standard brand: EIN / Tax ID
  email: zEmail,
  phone: z.string().max(25),
  mobilePhone: z.string().max(25).optional(), // sole proprietor: OTP target
  vertical: z.string().max(40).default('PROFESSIONAL'),
  street: zNonEmpty.max(160),
  city: zNonEmpty.max(80),
  state: zNonEmpty.max(40),
  postalCode: zNonEmpty.max(20),
  country: z.string().max(2).default('US'),
});
export type A2pBusiness = z.infer<typeof A2pBusinessSchema>;

export const A2pRegistrationSchema = EntityMetaSchema.extend({
  brandType: z.enum(['sole_proprietor', 'standard']).default('sole_proprietor'),
  status: z.enum(A2P_STATUS).default('not_started'),
  business: A2pBusinessSchema,
  brandId: z.string().optional(),
  campaignId: z.string().optional(),
  brandStatus: z.string().max(60).optional(),   // raw provider brand status (e.g. PENDING/VERIFIED)
  campaignStatus: z.string().max(60).optional(), // raw provider campaign status (e.g. ACTIVE)
  otpStatus: z.string().max(60).optional(),      // sole-prop OTP delivery status
  lastError: z.string().max(500).optional(),
  lastSyncedAt: z.string().optional(),
});
export type A2pRegistration = z.infer<typeof A2pRegistrationSchema>;

/** Self-service A2P registration input (from the Texting setup screen). */
export const StartA2pSchema = z.object({
  brandType: z.enum(['sole_proprietor', 'standard']).default('sole_proprietor'),
  displayName: zNonEmpty.max(160),
  legalName: z.string().max(160).optional(),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  ein: z.string().max(20).optional(),
  email: zEmail,
  phone: z.string().max(25),
  mobilePhone: z.string().max(25).optional(),
  vertical: z.string().max(40).optional(),
  street: zNonEmpty.max(160),
  city: zNonEmpty.max(80),
  state: zNonEmpty.max(40),
  postalCode: zNonEmpty.max(20),
  country: z.string().max(2).optional(),
}).refine(
  (v) => v.brandType !== 'sole_proprietor' || (v.firstName && v.lastName && v.mobilePhone),
  { message: 'Sole proprietor registration needs first name, last name, and a mobile number for verification' },
).refine(
  (v) => v.brandType !== 'standard' || (v.legalName && v.ein),
  { message: 'Standard brand registration needs the legal business name and EIN' },
);
export type StartA2pInput = z.infer<typeof StartA2pSchema>;

export const SendMessageSchema = z.object({
  customerId: zNonEmpty,
  channel: zEnum(['email', 'sms', 'note']),
  subject: z.string().max(200).optional(),
  body: zNonEmpty.max(20000),
  relatedType: z.enum(['lead', 'job', 'estimate', 'invoice']).optional(),
  relatedId: z.string().optional(),
});
export type SendMessageInput = z.infer<typeof SendMessageSchema>;

/** Automation rule: trigger -> conditions -> actions. */
export const AutomationConditionSchema = z.object({
  field: zNonEmpty.max(80), // e.g. "job.type", "customer.tag", "invoice.amountCents"
  op: z.enum(['eq', 'neq', 'gt', 'lt', 'contains', 'in']),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
});
export type AutomationCondition = z.infer<typeof AutomationConditionSchema>;

export const AutomationActionSchema = z.object({
  type: zEnum(AUTOMATION_ACTIONS),
  // Action-specific params; validated by the action handler at execution time.
  params: z.record(z.string(), z.unknown()).default({}),
});
export type AutomationActionDef = z.infer<typeof AutomationActionSchema>;

export const AutomationSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(120),
  enabled: z.boolean().default(true),
  trigger: zEnum(AUTOMATION_TRIGGERS),
  triggerParams: z.record(z.string(), z.unknown()).default({}), // e.g. {days: 30} for customer_inactive
  conditions: z.array(AutomationConditionSchema).default([]),
  actions: z.array(AutomationActionSchema).min(1),
});
export type Automation = z.infer<typeof AutomationSchema>;

export const CreateAutomationSchema = z.object({
  name: zNonEmpty.max(120),
  enabled: z.boolean().optional(),
  trigger: zEnum(AUTOMATION_TRIGGERS),
  triggerParams: z.record(z.string(), z.unknown()).optional(),
  conditions: z.array(AutomationConditionSchema).optional(),
  actions: z.array(AutomationActionSchema).min(1, 'Add at least one action'),
});
export type CreateAutomationInput = z.infer<typeof CreateAutomationSchema>;
export const UpdateAutomationSchema = CreateAutomationSchema.partial();
export type UpdateAutomationInput = z.infer<typeof UpdateAutomationSchema>;

/** Recurring service plan / membership. */
export const MembershipSchema = EntityMetaSchema.extend({
  customerId: z.string(),
  name: zNonEmpty.max(120), // "Comfort Club - Annual"
  active: z.boolean().default(true),
  billingInterval: zEnum(BILLING_INTERVAL),
  priceCents: z.number().int().min(0),
  serviceIntervalDays: z.number().int().positive(),
  includedServices: z.array(z.string().max(200)).default([]),
  discountPercent: z.number().min(0).max(100).default(0),
  equipmentIds: z.array(z.string()).default([]),
  startDate: z.string(), // ISO date
  renewalDate: z.string().optional(),
  nextServiceDate: z.string().optional(),
  autoRenew: z.boolean().default(true),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const CreateMembershipSchema = z.object({
  customerId: zNonEmpty,
  name: zNonEmpty.max(120),
  billingInterval: zEnum(BILLING_INTERVAL),
  priceCents: z.number().int().min(0),
  serviceIntervalDays: z.number().int().positive(),
  includedServices: z.array(z.string().max(200)).optional(),
  discountPercent: z.number().min(0).max(100).optional(),
  startDate: z.string(),
  autoRenew: z.boolean().optional(),
});
export type CreateMembershipInput = z.infer<typeof CreateMembershipSchema>;

/** Technician time entry. */
export const TimeEntrySchema = EntityMetaSchema.extend({
  userId: z.string(),
  jobId: z.string().optional(),
  kind: z.enum(['job', 'travel', 'break', 'other']).default('job'),
  clockIn: z.string(), // ISO timestamp
  clockOut: z.string().optional(),
  approved: z.boolean().default(false),
  approvedByUserId: z.string().optional(),
  note: z.string().max(500).optional(),
});
export type TimeEntry = z.infer<typeof TimeEntrySchema>;

/** Document metadata (blob lives in S3; access via presigned URL). */
export const DocumentSchema = EntityMetaSchema.extend({
  customerId: z.string().optional(),
  jobId: z.string().optional(),
  name: zNonEmpty.max(200),
  contentType: zNonEmpty.max(120),
  sizeBytes: z.number().int().min(0),
  s3Key: z.string(), // tenant-scoped private key
  kind: z.enum(['photo', 'pdf', 'contract', 'warranty', 'inspection', 'permit', 'other']).default('other'),
});
export type Document = z.infer<typeof DocumentSchema>;
